import { __assign, __awaiter, __generator, __read, __spreadArray } from "tslib";
import { isDocumentString, parseGraphQLSDL, asArray, getDocumentNodeFromSchema } from '@graphql-tools/utils';
import { isSchema, Kind } from 'graphql';
import isGlob from 'is-glob';
import { loadFile, loadFileSync } from './load-file';
import { stringToHash, useStack } from '../utils/helpers';
import { useCustomLoader, useCustomLoaderSync } from '../utils/custom-loader';
import { useQueue, useSyncQueue } from '../utils/queue';
import unixify from 'unixify';
import globby, { sync as globbySync } from 'globby';
var CONCURRENCY_LIMIT = 50;
export function collectSources(_a) {
    var _b;
    var pointerOptionMap = _a.pointerOptionMap, options = _a.options;
    return __awaiter(this, void 0, void 0, function () {
        var sources, globs, globOptions, queue, _c, addSource, addGlob, collect, pointer, pointerOptions, paths;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    sources = [];
                    globs = [];
                    globOptions = {};
                    queue = useQueue({ concurrency: CONCURRENCY_LIMIT });
                    _c = createHelpers({
                        sources: sources,
                        globs: globs,
                        options: options,
                        globOptions: globOptions,
                        stack: [collectDocumentString, collectGlob, collectCustomLoader, collectFallback],
                    }), addSource = _c.addSource, addGlob = _c.addGlob, collect = _c.collect;
                    for (pointer in pointerOptionMap) {
                        pointerOptions = __assign(__assign({}, ((_b = pointerOptionMap[pointer]) !== null && _b !== void 0 ? _b : {})), { unixify: unixify });
                        collect({
                            pointer: pointer,
                            pointerOptions: pointerOptions,
                            pointerOptionMap: pointerOptionMap,
                            options: options,
                            addSource: addSource,
                            addGlob: addGlob,
                            queue: queue.add,
                        });
                    }
                    if (!globs.length) return [3 /*break*/, 2];
                    includeIgnored({
                        options: options,
                        globs: globs,
                    });
                    return [4 /*yield*/, globby(globs, createGlobbyOptions(options))];
                case 1:
                    paths = _d.sent();
                    collectSourcesFromGlobals({
                        filepaths: paths,
                        options: options,
                        globOptions: globOptions,
                        pointerOptionMap: pointerOptionMap,
                        addSource: addSource,
                        queue: queue.add,
                    });
                    _d.label = 2;
                case 2: return [4 /*yield*/, queue.runAll()];
                case 3:
                    _d.sent();
                    return [2 /*return*/, sources];
            }
        });
    });
}
export function collectSourcesSync(_a) {
    var _b;
    var pointerOptionMap = _a.pointerOptionMap, options = _a.options;
    var sources = [];
    var globs = [];
    var globOptions = {};
    var queue = useSyncQueue();
    var _c = createHelpers({
        sources: sources,
        globs: globs,
        options: options,
        globOptions: globOptions,
        stack: [collectDocumentString, collectGlob, collectCustomLoaderSync, collectFallbackSync],
    }), addSource = _c.addSource, addGlob = _c.addGlob, collect = _c.collect;
    for (var pointer in pointerOptionMap) {
        var pointerOptions = __assign(__assign({}, ((_b = pointerOptionMap[pointer]) !== null && _b !== void 0 ? _b : {})), { unixify: unixify });
        collect({
            pointer: pointer,
            pointerOptions: pointerOptions,
            pointerOptionMap: pointerOptionMap,
            options: options,
            addSource: addSource,
            addGlob: addGlob,
            queue: queue.add,
        });
    }
    if (globs.length) {
        includeIgnored({
            options: options,
            globs: globs,
        });
        var paths = globbySync(globs, createGlobbyOptions(options));
        collectSourcesFromGlobalsSync({
            filepaths: paths,
            options: options,
            globOptions: globOptions,
            pointerOptionMap: pointerOptionMap,
            addSource: addSource,
            queue: queue.add,
        });
    }
    queue.runAll();
    return sources;
}
//
function createHelpers(_a) {
    var sources = _a.sources, globs = _a.globs, options = _a.options, globOptions = _a.globOptions, stack = _a.stack;
    var addSource = function (_a) {
        var pointer = _a.pointer, source = _a.source, noCache = _a.noCache;
        sources.push(source);
        if (!noCache) {
            options.cache[pointer] = source;
        }
    };
    var collect = useStack.apply(void 0, __spreadArray([], __read(stack)));
    var addGlob = function (_a) {
        var pointerOptions = _a.pointerOptions, pointer = _a.pointer;
        globs.push(pointer);
        Object.assign(globOptions, pointerOptions);
    };
    return {
        addSource: addSource,
        collect: collect,
        addGlob: addGlob,
    };
}
function includeIgnored(_a) {
    var options = _a.options, globs = _a.globs;
    if (options.ignore) {
        var ignoreList = asArray(options.ignore)
            .map(function (g) { return "!(" + g + ")"; })
            .map(unixify);
        if (ignoreList.length > 0) {
            globs.push.apply(globs, __spreadArray([], __read(ignoreList)));
        }
    }
}
function createGlobbyOptions(options) {
    return __assign(__assign({ absolute: true }, options), { ignore: [] });
}
function collectSourcesFromGlobals(_a) {
    var filepaths = _a.filepaths, options = _a.options, globOptions = _a.globOptions, pointerOptionMap = _a.pointerOptionMap, addSource = _a.addSource, queue = _a.queue;
    var collectFromGlobs = useStack(collectCustomLoader, collectFallback);
    for (var i = 0; i < filepaths.length; i++) {
        var pointer = filepaths[i];
        collectFromGlobs({
            pointer: pointer,
            pointerOptions: globOptions,
            pointerOptionMap: pointerOptionMap,
            options: options,
            addSource: addSource,
            addGlob: function () {
                throw new Error("I don't accept any new globs!");
            },
            queue: queue,
        });
    }
}
function collectSourcesFromGlobalsSync(_a) {
    var filepaths = _a.filepaths, options = _a.options, globOptions = _a.globOptions, pointerOptionMap = _a.pointerOptionMap, addSource = _a.addSource, queue = _a.queue;
    var collectFromGlobs = useStack(collectCustomLoaderSync, collectFallbackSync);
    for (var i = 0; i < filepaths.length; i++) {
        var pointer = filepaths[i];
        collectFromGlobs({
            pointer: pointer,
            pointerOptions: globOptions,
            pointerOptionMap: pointerOptionMap,
            options: options,
            addSource: addSource,
            addGlob: function () {
                throw new Error("I don't accept any new globs!");
            },
            queue: queue,
        });
    }
}
function addResultOfCustomLoader(_a) {
    var pointer = _a.pointer, result = _a.result, addSource = _a.addSource;
    if (isSchema(result)) {
        addSource({
            source: {
                location: pointer,
                schema: result,
                document: getDocumentNodeFromSchema(result),
            },
            pointer: pointer,
            noCache: true,
        });
    }
    else if (result.kind && result.kind === Kind.DOCUMENT) {
        addSource({
            source: {
                document: result,
                location: pointer,
            },
            pointer: pointer,
        });
    }
    else if (result.document) {
        addSource({
            source: __assign({ location: pointer }, result),
            pointer: pointer,
        });
    }
}
function collectDocumentString(_a, next) {
    var pointer = _a.pointer, pointerOptions = _a.pointerOptions, options = _a.options, addSource = _a.addSource, queue = _a.queue;
    if (isDocumentString(pointer)) {
        return queue(function () {
            var source = parseGraphQLSDL(stringToHash(pointer) + ".graphql", pointer, __assign(__assign({}, options), pointerOptions));
            addSource({
                source: source,
                pointer: pointer,
            });
        });
    }
    next();
}
function collectGlob(_a, next) {
    var pointer = _a.pointer, pointerOptions = _a.pointerOptions, addGlob = _a.addGlob;
    if (isGlob(pointerOptions.unixify(pointer))) {
        return addGlob({
            pointer: pointerOptions.unixify(pointer),
            pointerOptions: pointerOptions,
        });
    }
    next();
}
function collectCustomLoader(_a, next) {
    var _this = this;
    var pointer = _a.pointer, pointerOptions = _a.pointerOptions, queue = _a.queue, addSource = _a.addSource, options = _a.options, pointerOptionMap = _a.pointerOptionMap;
    if (pointerOptions.loader) {
        return queue(function () { return __awaiter(_this, void 0, void 0, function () {
            var loader, result;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, useCustomLoader(pointerOptions.loader, options.cwd)];
                    case 1:
                        loader = _a.sent();
                        return [4 /*yield*/, loader(pointer, __assign(__assign({}, options), pointerOptions), pointerOptionMap)];
                    case 2:
                        result = _a.sent();
                        if (!result) {
                            return [2 /*return*/];
                        }
                        addResultOfCustomLoader({ pointer: pointer, result: result, addSource: addSource });
                        return [2 /*return*/];
                }
            });
        }); });
    }
    next();
}
function collectCustomLoaderSync(_a, next) {
    var pointer = _a.pointer, pointerOptions = _a.pointerOptions, queue = _a.queue, addSource = _a.addSource, options = _a.options, pointerOptionMap = _a.pointerOptionMap;
    if (pointerOptions.loader) {
        return queue(function () {
            var loader = useCustomLoaderSync(pointerOptions.loader, options.cwd);
            var result = loader(pointer, __assign(__assign({}, options), pointerOptions), pointerOptionMap);
            if (result) {
                addResultOfCustomLoader({ pointer: pointer, result: result, addSource: addSource });
            }
        });
    }
    next();
}
function collectFallback(_a) {
    var _this = this;
    var queue = _a.queue, pointer = _a.pointer, options = _a.options, pointerOptions = _a.pointerOptions, addSource = _a.addSource;
    return queue(function () { return __awaiter(_this, void 0, void 0, function () {
        var source;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, loadFile(pointer, __assign(__assign({}, options), pointerOptions))];
                case 1:
                    source = _a.sent();
                    if (source) {
                        addSource({ source: source, pointer: pointer });
                    }
                    return [2 /*return*/];
            }
        });
    }); });
}
function collectFallbackSync(_a) {
    var queue = _a.queue, pointer = _a.pointer, options = _a.options, pointerOptions = _a.pointerOptions, addSource = _a.addSource;
    return queue(function () {
        var source = loadFileSync(pointer, __assign(__assign({}, options), pointerOptions));
        if (source) {
            addSource({ source: source, pointer: pointer });
        }
    });
}
//# sourceMappingURL=collect-sources.js.map